import { getFile, FileRecord } from "../services/filesService";
import { loadSceneFromCloud } from "./cloudScene";
import { ImportedDataState } from "../../packages/excalidraw/data/types";
import { isFileOwner } from "./shareRouting";

/**
 * Owner-gated load for the private editor route `/f/<id>`.
 *
 * Unlike the public `/share/<id>` loader (`loadShareFile`), this resolves ONLY
 * for the signed-in owner. The file's metadata row is fetched first and
 * ownership is checked BEFORE the scene is ever requested, so a non-owner — even
 * of a PUBLIC file, whose row is readable via RLS and whose scene GET would
 * succeed — never receives an editable scene to hydrate. They collapse to the
 * uniform `notfound` (no existence leak) exactly like a missing/private file.
 *
 * Gating before the scene fetch is deliberate: it means the editor never
 * hydrates for a non-owner, and the autosave/buffer pipeline never gets a
 * baseline to arm — so no PUTs are attempted that would silently 404 and get
 * mislabelled "Offline — kept locally".
 */

/**
 * - `ready`    — owner + a scene blob exists; hydrate it.
 * - `empty`    — owner, the row exists, but no scene blob yet (worker 204). An
 *                orphan first-save row self-heals: open it as an empty canvas
 *                the user can save into, instead of a dead 404 card.
 * - `error`    — a transient/server failure reading the scene (network, 5xx,
 *                503, malformed). Distinct from `notfound` so the caller can
 *                offer a retry rather than claim the file doesn't exist.
 * - `notfound` — genuinely missing / private / not the owner (uniform 404).
 */
export type CloudEditorFileStatus = "ready" | "empty" | "error" | "notfound";

export interface CloudEditorFileState {
  status: CloudEditorFileStatus;
  file: FileRecord | null;
  scene: ImportedDataState | null;
}

const NOT_FOUND: CloudEditorFileState = {
  status: "notfound",
  file: null,
  scene: null,
};

/** An explicitly empty-but-valid scene for the self-healing `empty` case. */
const EMPTY_SCENE: ImportedDataState = {
  elements: [],
  appState: null,
  files: undefined,
};

export const loadCloudEditorFile = async (
  fileId: string,
  userId: string | null | undefined,
  accessToken?: string,
): Promise<CloudEditorFileState> => {
  const file = await getFile(fileId).catch(() => null);
  if (!isFileOwner(userId, file)) {
    return NOT_FOUND;
  }
  const { data, status } = await loadSceneFromCloud(fileId, accessToken);

  if (status === 200 && data) {
    return { status: "ready", file, scene: data };
  }
  if (status === 204) {
    // Row exists, blob missing → self-heal into an empty canvas.
    return { status: "empty", file, scene: data ?? EMPTY_SCENE };
  }
  if (status === 404) {
    // Genuine not-found (or the blob was deleted): uniform 404.
    return NOT_FOUND;
  }
  // Anything else (network 0, 5xx, 503, malformed body) is retryable — do NOT
  // collapse a transient blip into "this file does not exist".
  return { status: "error", file, scene: null };
};
